import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Loader2, Check, Plus, Users, Mail, Upload, Save, Send, Lock, Paperclip, Wrench, Sparkles, Megaphone, Trash2, CreditCard, RefreshCw, AlertTriangle } from "lucide-react";
import { API, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import GauntletQuestionAdmin from "@/components/GauntletQuestionAdmin";
import YoutubeContentAdmin from "@/components/YoutubeContentAdmin";
import CreatorRosterAdmin from "@/components/CreatorRosterAdmin";
import MediaUploadsAdmin from "@/components/MediaUploadsAdmin";
import NewsExportAdmin from "@/components/NewsExportAdmin";
import SocialConnectionsAdmin from "@/components/SocialConnectionsAdmin";
import AutoRepliesAdmin from "@/components/AutoRepliesAdmin";

import GrowthReport from "@/components/GrowthReport";

async function growthRequest(method, path, body) {
  const res = await apiFetch(`/admin${path}`, { method, headers: { "Content-Type": "application/json" }, ...(body ? { body: JSON.stringify(body) } : {}) });
  const data = await res.json();
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Report unavailable");
  return data;
}

const EMPTY = { name: "", game: "Balatro", format: "Single Elimination", description: "", prize: "", start_at: "", max_players: 64, rules: "" };
const EMPTY_NEWSLETTER = { subject: "", preview_text: "", body: "", attachment_name: null, attachment_type: null, attachment_base64: null };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [mediaUploads, setMediaUploads] = useState([]);
  const [pending, setPending] = useState([]);
  const [roster, setRoster] = useState({ signups: [], total: 0, selected: 0, event: null });
  const [checked, setChecked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [newsletter, setNewsletter] = useState({ subscribers: [], total: 0, draft: null });
  const [newsletterForm, setNewsletterForm] = useState(EMPTY_NEWSLETTER);
  const [newsletterBusy, setNewsletterBusy] = useState(false);
  const [importConsent, setImportConsent] = useState(false);
  const [importEmails, setImportEmails] = useState([]);
  const [maintenanceTask, setMaintenanceTask] = useState("");
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [promoResetBusy, setPromoResetBusy] = useState(false);

  const loadPending = useCallback(() => {
    apiFetch("/admin/results").then((r) => (r.ok ? r.json() : { results: [] })).then((d) => setPending(d.results || [])).catch(() => {});
  }, []);
  const loadRoster = useCallback(() => {
    apiFetch("/admin/signups").then((r) => (r.ok ? r.json() : null)).then((d) => d && setRoster(d)).catch(() => {});
  }, []);
  const loadMaintenance = useCallback(() => {
    fetch(`${API}/admin/maintenance`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { tasks: [] }))
      .then((d) => setMaintenanceTasks(d.tasks || []))
      .catch(() => {});
  }, []);
  const loadNewsletter = useCallback(() => {
    fetch(`${API}/admin/newsletter`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setNewsletter(d);
        if (d.draft) setNewsletterForm({ ...EMPTY_NEWSLETTER, ...d.draft });
      })
      .catch(() => {});
  }, []);
  const loadBillingStatus = useCallback(async () => {
    setBillingBusy(true);
    try {
      const res = await fetch(`${API}/admin/billing/status`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Square status is unavailable.");
      setBillingStatus(data);
    } catch (error) {
      setBillingStatus(null);
      toast.error(error.message || "Could not verify Square.");
    } finally {
      setBillingBusy(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) { navigate("/"); return; }
    loadPending(); loadRoster(); loadNewsletter(); loadMaintenance(); loadBillingStatus();
  }, [user, loading, navigate, loadPending, loadRoster, loadNewsletter, loadMaintenance, loadBillingStatus]);

  if (loading || !user) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-gp-blue" size={30} /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_at) { toast.error("Name and start date are required."); return; }
    setBusy(true);
    try {
      const body = { ...form, max_players: Number(form.max_players) || 64, start_at: new Date(form.start_at).toISOString() };
      const res = await apiFetch("/tournaments", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success("Tournament created."); setForm(EMPTY);
    } catch { toast.error("Could not create tournament."); } finally { setBusy(false); }
  };

  const verify = async (rid) => {
    try {
      const res = await apiFetch(`/results/${rid}/verify`, { method: "POST" });
      if (!res.ok) throw new Error();
      toast.success("Verified — points awarded."); loadPending();
    } catch { toast.error("Could not verify."); }
  };

  const allChecked = roster.signups.length > 0 && checked.size === roster.signups.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(roster.signups.map((s) => s.id)));
  const toggleOne = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const mark = async (selected) => {
    if (checked.size === 0) { toast.info("Select at least one player."); return; }
    try {
      const res = await apiFetch("/admin/signups/select", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids: [...checked], selected }) });
      if (!res.ok) throw new Error();
      toast.success(selected ? `Added ${checked.size} to the roster.` : `Removed ${checked.size} from the roster.`);
      setChecked(new Set()); loadRoster();
    } catch { toast.error("Could not update selection."); }
  };

  const createMaintenancePlan = async () => {
    if (maintenanceTask.trim().length < 10) { toast.error("Describe the maintenance task in a little more detail."); return; }
    setMaintenanceBusy(true);
    try {
      const res = await fetch(`${API}/admin/maintenance/plan`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task: maintenanceTask }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Yorick could not prepare the plan.");
      toast.success("Yorick prepared a maintenance plan for your approval.");
      setMaintenanceTask("");
      loadMaintenance();
    } catch (error) { toast.error(error.message || "Could not create the maintenance plan."); } finally { setMaintenanceBusy(false); }
  };

  const setNewsletterField = (key) => (e) => setNewsletterForm((prev) => ({ ...prev, [key]: e.target.value }));
  const addNewsletterEmoji = (emoji) => setNewsletterForm((prev) => ({ ...prev, body: `${prev.body}${prev.body && !prev.body.endsWith(" ") ? " " : ""}${emoji}` }));

  const readLeadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const matches = String(reader.result || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
      const unique = [...new Set(matches.map((email) => email.toLowerCase()))];
      setImportEmails(unique);
      unique.length ? toast.success(`Found ${unique.length} unique email address${unique.length === 1 ? "" : "es"}.`) : toast.error("No email addresses were found in that file.");
    };
    reader.readAsText(file);
  };

  const importLeads = async () => {
    if (!importConsent) { toast.error("Confirm that every contact consented to receive email."); return; }
    if (importEmails.length === 0) { toast.error("Choose a CSV or text file containing email addresses."); return; }
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/import`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: importEmails, consent_confirmed: true, source: "admin_csv" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed.");
      toast.success(`Imported ${data.imported} consented contact${data.imported === 1 ? "" : "s"}.`);
      setImportEmails([]); setImportConsent(false); loadNewsletter();
    } catch (error) { toast.error(error.message || "Could not import contacts."); } finally { setNewsletterBusy(false); }
  };

  const readAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Choose a file that is 4 MB or smaller."); e.target.value = ""; return; }
    const allowed = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Use a PNG, JPG, GIF, WebP, or PDF file."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => setNewsletterForm((prev) => ({
      ...prev,
      attachment_name: file.name,
      attachment_type: file.type,
      attachment_base64: String(reader.result).split(",", 2)[1],
    }));
    reader.readAsDataURL(file);
  };

  const newsletterPayload = () => ({
    subject: newsletterForm.subject,
    preview_text: newsletterForm.preview_text,
    body: newsletterForm.body,
    attachment_name: newsletterForm.attachment_name,
    attachment_type: newsletterForm.attachment_type,
    attachment_base64: newsletterForm.attachment_base64,
  });

  const saveNewsletterDraft = async () => {
    if (!newsletterForm.subject.trim() || !newsletterForm.body.trim()) { toast.error("Add a subject and newsletter message first."); return; }
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/draft`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed.");
      toast.success("Newsletter draft saved.");
      loadNewsletter();
    } catch (error) { toast.error(error.message || "Could not save the draft."); } finally { setNewsletterBusy(false); }
  };

  const sendNewsletterTest = async () => {
    if (!newsletterForm.subject.trim() || !newsletterForm.body.trim()) { toast.error("Add a subject and newsletter message first."); return; }
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/test`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Test send failed.");
      toast.success(`Test email sent to ${data.sent_to}.`);
      loadNewsletter();
    } catch (error) { toast.error(error.message || "Could not send the test email."); } finally { setNewsletterBusy(false); }
  };

  const publishNewsletterToWeb = async () => {
    if (!newsletterForm.subject.trim() || !newsletterForm.body.trim()) { toast.error("Add a headline and message first."); return; }
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/publish-web`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Publish failed.");
      toast.success("Published to the visitor message board and notification bell.");
      loadNewsletter();
    } catch (error) { toast.error(error.message || "Could not publish the visitor message."); } finally { setNewsletterBusy(false); }
  };

  const resetYorickPromos = async () => {
    if (!window.confirm("Restart Yorick's guide-promo catalog from item one, spaced 72h apart starting now?")) return;
    setPromoResetBusy(true);
    try {
      const res = await fetch(`${API}/admin/yorick/promos/reset`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed.");
      toast.success(`Cadence reset — ${data.scheduled} posts scheduled from now.`);
    } catch (error) { toast.error(error.message || "Could not reset the promo cadence."); } finally { setPromoResetBusy(false); }
  };

  const clearNewsletterFromWeb = async () => {
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/publish-web`, { method: "DELETE", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Clear failed.");
      toast.success("Visitor message cleared.");
      loadNewsletter();
    } catch (error) { toast.error(error.message || "Could not clear the visitor message."); } finally { setNewsletterBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 md:px-8 py-12 md:py-16">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="text-gp-gold" size={26} />
        <h1 className="font-unbounded font-black tracking-tighter text-4xl uppercase">Admin Dashboard</h1>
      </div>

      <GauntletQuestionAdmin />

      <MediaUploadsAdmin onUploadsChanged={setMediaUploads} />

      <GrowthReport request={growthRequest} />
      <YoutubeContentAdmin uploads={mediaUploads} />

      <CreatorRosterAdmin />

      <section className="glass rounded-3xl p-7 mb-8 border border-white/10" data-testid="admin-promo-reset">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-gp-gold"><RefreshCw size={20} /><span className="font-unbounded font-bold uppercase">Yorick Promo Cadence</span></div>
            <p className="mt-2 max-w-2xl text-sm text-white/55">
              Restarts the Balatro guide-promo catalog from the first item, spaced 72h apart starting now —
              every piece of existing material posts to the news board (and Reddit/X once connected) again.
            </p>
          </div>
          <Button onClick={resetYorickPromos} disabled={promoResetBusy} data-testid="reset-yorick-promos-btn">
            {promoResetBusy ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
            Restart from the beginning
          </Button>
        </div>
      </section>

      <SocialConnectionsAdmin />

      <AutoRepliesAdmin />

      <section className="glass rounded-3xl p-7 mb-8 border border-white/10">
        <NewsExportAdmin />
      </section>

      <section className="glass rounded-3xl p-7 mb-8 border border-gp-blue/25" data-testid="admin-square-status">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-gp-blue"><CreditCard size={20} /><span className="font-unbounded font-bold uppercase">Square Payments</span></div>
            <p className="mt-2 max-w-2xl text-sm text-white/55">Sandbox until a real non-admin purchase is proven. Live charges stay locked even if production credentials are present.</p>
          </div>
          <Button type="button" onClick={loadBillingStatus} disabled={billingBusy} variant="outline" className="rounded-full border-gp-blue/30 bg-gp-blue/10 text-gp-blue hover:bg-gp-blue/20">
            <RefreshCw size={15} className={`mr-2 ${billingBusy ? "animate-spin" : ""}`} /> Recheck Square
          </Button>
        </div>

        {billingBusy && !billingStatus ? (
          <div className="mt-5 flex items-center gap-2 text-sm text-white/55"><Loader2 size={16} className="animate-spin" /> Checking Square production settings…</div>
        ) : billingStatus ? (
          <div className="mt-5">
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${billingStatus.ready ? "bg-gp-green/15 text-gp-green" : "bg-amber-400/10 text-amber-300"}`}>
              {billingStatus.ready ? <Check size={15} /> : <AlertTriangle size={15} />}
              {billingStatus.ready
                ? (billingStatus.sandbox_mode ? "Sandbox catalog ready — live charges locked" : "Ready to accept recurring payments")
                : "Square setup still needs attention"}
            </div>
            <p className="mt-2 text-xs uppercase tracking-widest text-white/40">Environment: {billingStatus.environment}{billingStatus.live_charges_allowed ? " · live charges allowed" : " · live charges locked"}</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                ["Square API reachable", billingStatus.square_reachable],
                ["Location verified", billingStatus.location_verified],
              ].map(([label, ok]) => (
                <div key={label} className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm">
                  {ok ? <Check size={15} className="text-gp-green" /> : <AlertTriangle size={15} className="text-amber-300" />}
                  <span className="text-white/70">{label}</span>
                </div>
              ))}
              {Object.entries(billingStatus.configured || {}).map(([key, ok]) => (
                <div key={key} className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm">
                  {ok ? <Check size={15} className="text-gp-green" /> : <AlertTriangle size={15} className="text-amber-300" />}
                  <span className="capitalize text-white/70">{key.replaceAll("_", " ")}</span>
                </div>
              ))}
              {Object.entries(billingStatus.catalog || {}).map(([plan, details]) => (
                <div key={plan} className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm">
                  {details.matches_app_price ? <Check size={15} className="text-gp-green" /> : <AlertTriangle size={15} className="text-amber-300" />}
                  <span className="capitalize text-white/70">{plan}: {details.amount == null ? "not found" : `$${(details.amount / 100).toFixed(2)} ${details.currency || ""}`}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-5 text-sm text-white/50">Square status has not been checked yet.</p>
        )}
      </section>

      <section className="glass rounded-3xl p-7 mb-8 border border-gp-gold/20">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-gp-gold"><Wrench size={20} /><span className="font-unbounded font-bold uppercase">Yorick Maintenance Studio</span></div>
            <p className="text-white/55 text-sm mt-2 max-w-2xl">Describe a page, content, SEO, newsletter, or maintenance change. Admin use is unlimited. Yorick prepares an exact plan for review; nothing is published without your approval.</p>
          </div>
          <span className="rounded-full bg-gp-gold/10 text-gp-gold px-3 py-1 text-xs font-semibold whitespace-nowrap">Admin unlimited</span>
        </div>
        <Textarea value={maintenanceTask} onChange={(e) => setMaintenanceTask(e.target.value)} placeholder="Example: Create a Balatro beginner guide page targeting the question ‘What are the best Jokers for beginners?’ Include title, meta description, FAQ schema, and a waitlist call to action." className="mt-5 min-h-32 bg-black/30 border-white/10 rounded-2xl" />
        <Button onClick={createMaintenancePlan} disabled={maintenanceBusy} className="mt-3 rounded-full bg-gp-gold text-black hover:bg-gp-gold/90 font-bold px-6">
          {maintenanceBusy ? <Loader2 className="animate-spin mr-2" size={17} /> : <Sparkles className="mr-2" size={17} />} Prepare maintenance plan
        </Button>
        {maintenanceTasks.length > 0 && (
          <div className="mt-6 space-y-4">
            <h2 className="font-unbounded font-bold text-sm uppercase text-white/70">Awaiting your approval</h2>
            {maintenanceTasks.slice(0, 5).map((item) => (
              <article key={item.id} className="rounded-2xl border border-white/10 bg-black/20 p-5">
                <div className="flex items-center justify-between gap-3"><p className="font-semibold">{item.task}</p><span className="text-xs text-gp-gold whitespace-nowrap">Review only</span></div>
                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/65">{item.plan}</pre>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Roster / signups */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="admin-roster">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-unbounded font-bold text-lg flex items-center gap-2"><Users size={18} className="text-gp-blue" /> Registrations — {roster.event?.name || "Next event"}</h2>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-white/50">{roster.total} registered · {roster.selected} on roster</span>
            <Button size="sm" onClick={() => mark(true)} data-testid="roster-add" className="rounded-full bg-gp-green/20 text-gp-green hover:bg-gp-green/30 h-8">Add to roster</Button>
            <Button size="sm" onClick={() => mark(false)} variant="outline" className="rounded-full border-white/15 bg-white/5 h-8">Remove</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/10">
                <th className="w-10 py-2 text-left"><input type="checkbox" data-testid="roster-select-all" checked={allChecked} onChange={toggleAll} className="accent-[#19c37d] h-4 w-4" /></th>
                <th className="py-2 text-left font-medium">Name</th>
                <th className="py-2 text-left font-medium">Email</th>
                <th className="py-2 text-left font-medium hidden sm:table-cell">Discord</th>
                <th className="py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody data-testid="roster-list">
              {roster.signups.length === 0 && <tr><td colSpan={5} className="py-6 text-white/40">No registrations yet.</td></tr>}
              {roster.signups.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="py-2.5"><input type="checkbox" data-testid={`roster-check-${s.id}`} checked={checked.has(s.id)} onChange={() => toggleOne(s.id)} className="accent-[#19c37d] h-4 w-4" /></td>
                  <td className="py-2.5 font-medium">{s.name}</td>
                  <td className="py-2.5 text-white/70">{s.email}</td>
                  <td className="py-2.5 text-white/60 hidden sm:table-cell">{s.discord || "—"}</td>
                  <td className="py-2.5">{s.selected ? <span className="text-xs px-2 py-0.5 rounded-full bg-gp-green/15 text-gp-green">On roster</span> : <span className="text-xs text-white/40">Registered</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Newsletter manager */}
      <section className="glass rounded-2xl p-6 mb-6" data-testid="admin-newsletter">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-unbounded font-bold text-lg flex items-center gap-2"><Mail size={18} className="text-gp-green" /> Newsletter Manager</h2>
            <p className="mt-1 text-sm text-white/50">{newsletter.total} consented subscriber{newsletter.total === 1 ? "" : "s"} · drafts and test emails only</p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-gp-gold/10 px-3 py-1 text-xs text-gp-gold"><Lock size={12} /> Bulk sending locked</span>
        </div>

        <div className="mt-6 grid xl:grid-cols-[0.8fr_1.2fr] gap-6">
          <div className="space-y-5">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <h3 className="font-semibold flex items-center gap-2"><Upload size={16} className="text-gp-blue" /> Import consented leads</h3>
              <p className="mt-1 text-xs leading-relaxed text-white/45">Upload a CSV or text file. Email addresses are extracted, normalized, and de-duplicated. Only import people who agreed to receive marketing email.</p>
              <Input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={readLeadFile} className="mt-4 bg-black/30 border-white/10 file:text-white" />
              {importEmails.length > 0 && <p className="mt-2 text-sm text-gp-green">{importEmails.length} unique address{importEmails.length === 1 ? "" : "es"} ready</p>}
              <label className="mt-4 flex items-start gap-2 text-xs text-white/65">
                <input type="checkbox" checked={importConsent} onChange={(e) => setImportConsent(e.target.checked)} className="mt-0.5 accent-[#19c37d]" />
                <span>I confirm every imported contact consented to receive GamePlat email.</span>
              </label>
              <Button type="button" onClick={importLeads} disabled={newsletterBusy || importEmails.length === 0} className="mt-4 w-full rounded-full bg-gp-blue hover:bg-gp-blue/90">
                {newsletterBusy ? <Loader2 className="animate-spin" size={16} /> : "Import contacts"}
              </Button>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4 max-h-64 overflow-y-auto gp-scroll">
              <h3 className="font-semibold">Recent subscribers</h3>
              <div className="mt-3 space-y-2">
                {newsletter.subscribers.length === 0 && <p className="text-sm text-white/40">No newsletter subscribers yet.</p>}
                {newsletter.subscribers.slice(0, 50).map((subscriber) => (
                  <div key={subscriber.email} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-white/75">{subscriber.email}</span>
                    <span className="shrink-0 text-xs text-white/35">{subscriber.source || "website"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Input value={newsletterForm.subject} onChange={setNewsletterField("subject")} placeholder="Newsletter subject" maxLength={200} className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <Input value={newsletterForm.preview_text} onChange={setNewsletterField("preview_text")} placeholder="Inbox preview text (optional)" maxLength={250} className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <Textarea value={newsletterForm.body} onChange={setNewsletterField("body")} placeholder="Write the newsletter message…" rows={8} maxLength={20000} className="bg-black/30 border-white/10 rounded-xl resize-y" />
            <div className="flex flex-wrap items-center gap-2" aria-label="Common emojis">
              <span className="text-xs text-white/45 mr-1">Add emoji:</span>
              {["🎮", "🏆", "🔥", "📣", "🎉", "⭐", "✅", "💡", "👀", "❤️"].map((emoji) => (
                <button key={emoji} type="button" onClick={() => addNewsletterEmoji(emoji)} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 bg-white/5 text-lg hover:bg-white/10" aria-label={`Add ${emoji}`}>{emoji}</button>
              ))}
            </div>

            <div className="rounded-xl border border-dashed border-white/15 bg-black/20 p-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <Paperclip size={16} className="text-gp-blue" /> Add image or PDF
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" onChange={readAttachment} className="sr-only" />
              </label>
              <p className="mt-1 text-xs text-white/40">PNG, JPG, GIF, WebP, or PDF · maximum 4 MB</p>
              {newsletterForm.attachment_name && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white/5 px-3 py-2 text-sm">
                  <span className="truncate">{newsletterForm.attachment_name}</span>
                  <button type="button" onClick={() => setNewsletterForm((prev) => ({ ...prev, attachment_name: null, attachment_type: null, attachment_base64: null }))} className="text-xs text-gp-red hover:underline">Remove</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-xs uppercase tracking-widest text-gp-gold">Preview</p>
              <h3 className="mt-2 text-xl font-bold">{newsletterForm.subject || "Your newsletter subject"}</h3>
              {newsletterForm.preview_text && <p className="mt-1 text-sm text-white/45">{newsletterForm.preview_text}</p>}
              <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-white/70">{newsletterForm.body || "Your message will appear here."}</p>
              {newsletterForm.attachment_name && <p className="mt-4 text-xs text-gp-blue">Attachment: {newsletterForm.attachment_name}</p>}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button type="button" onClick={saveNewsletterDraft} disabled={newsletterBusy} variant="outline" className="flex-1 rounded-full border-white/15 bg-white/5">
                <Save size={16} className="mr-2" /> Save draft
              </Button>
              <Button type="button" onClick={sendNewsletterTest} disabled={newsletterBusy} className="flex-1 rounded-full bg-gp-green text-black hover:bg-gp-green/90">
                {newsletterBusy ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} className="mr-2" /> Send test to me</>}
              </Button>
            </div>
            <div className="rounded-xl border border-gp-blue/20 bg-gp-blue/5 p-4">
              <div className="flex items-start gap-3">
                <Megaphone size={18} className="mt-0.5 shrink-0 text-gp-blue" />
                <div>
                  <p className="font-semibold">Visitor message board</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/50">Publish replaces the current visitor message. It appears in News and creates a new notification in the bell. It does not email subscribers.</p>
                </div>
              </div>
              {newsletter.web_post && <p className="mt-3 text-xs text-gp-green">Currently live: {newsletter.web_post.title}</p>}
              <div className="mt-4 flex flex-wrap gap-3">
                <Button type="button" onClick={publishNewsletterToWeb} disabled={newsletterBusy} className="flex-1 rounded-full bg-gp-blue hover:bg-gp-blue/90">
                  <Megaphone size={16} className="mr-2" /> {newsletter.web_post ? "Replace visitor message" : "Publish to visitors"}
                </Button>
                <Button type="button" onClick={clearNewsletterFromWeb} disabled={newsletterBusy || !newsletter.web_post} variant="outline" className="rounded-full border-white/15 bg-white/5 text-gp-red">
                  <Trash2 size={16} className="mr-2" /> Clear
                </Button>
              </div>
            </div>
            <p className="text-xs text-white/40">Save draft keeps the message private. Test email goes only to your signed-in admin email. Publish to visitors uses the in-app News page and notification bell; it does not email subscribers.</p>
          </div>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={create} className="glass rounded-2xl p-6 space-y-3" data-testid="admin-create-form">
          <h2 className="font-unbounded font-bold text-lg flex items-center gap-2"><Plus size={18} className="text-gp-blue" /> Create tournament</h2>
          <Input data-testid="admin-name" value={form.name} onChange={set("name")} placeholder="Tournament name" className="bg-black/30 border-white/10 h-11 rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="admin-game" value={form.game} onChange={set("game")} placeholder="Game" className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <Input value={form.format} onChange={set("format")} placeholder="Format" className="bg-black/30 border-white/10 h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.prize} onChange={set("prize")} placeholder="Prize" className="bg-black/30 border-white/10 h-11 rounded-xl" />
            <Input type="number" value={form.max_players} onChange={set("max_players")} placeholder="Max players" className="bg-black/30 border-white/10 h-11 rounded-xl" />
          </div>
          <Input data-testid="admin-start" type="datetime-local" value={form.start_at} onChange={set("start_at")} className="bg-black/30 border-white/10 h-11 rounded-xl" />
          <Textarea value={form.description} onChange={set("description")} placeholder="Description" rows={2} className="bg-black/30 border-white/10 rounded-xl resize-none" />
          <Button type="submit" data-testid="admin-create-submit" disabled={busy} className="w-full rounded-full py-5 bg-gp-blue hover:bg-gp-blue/90 font-bold">{busy ? <Loader2 className="animate-spin" size={16} /> : "Create tournament"}</Button>
        </form>

        <div className="glass rounded-2xl p-6">
          <h2 className="font-unbounded font-bold text-lg">Pending results ({pending.length})</h2>
          <div className="mt-4 space-y-2 max-h-[360px] overflow-y-auto gp-scroll" data-testid="admin-pending">
            {pending.length === 0 && <p className="text-white/40 text-sm">No results waiting for review.</p>}
            {pending.map((r) => (
              <div key={r.id} className="rounded-xl bg-black/25 px-4 py-3">
                <p className="text-xs text-white/40">{r.tournament_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-medium">{r.handle} vs {r.opponent} · <span className="text-white/60">{r.score}</span> · <span className={r.result === "win" ? "text-gp-green" : "text-gp-red"}>{r.result}</span></span>
                  <Button size="sm" onClick={() => verify(r.id)} data-testid={`verify-${r.id}`} className="rounded-full bg-gp-green/20 text-gp-green hover:bg-gp-green/30 h-8 px-3"><Check size={14} className="mr-1" /> Verify</Button>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate("/news")} variant="outline" className="mt-5 w-full rounded-full border-white/15 bg-white/5">Post a news update →</Button>
        </div>
      </div>
    </div>
  );
}
